/** 应用外壳与导航（参考 v0 设计：固定顶栏 + 步骤指示 + 图标导航）。 */
import { useEffect, useState } from 'react';
import { Settings2, Zap, Play, FlaskConical, BarChart3, Activity, Gauge } from 'lucide-react';
import { ScenarioConfigPage } from './pages/ScenarioConfigPage';
import { StrategyPage } from './pages/StrategyPage';
import { SimulationPage } from './pages/SimulationPage';
import { BatchPage } from './pages/BatchPage';
import { ComparePage } from './pages/ComparePage';
import { PerfBenchmark } from './PerfBenchmark';
import { ErrorBoundary } from './ErrorBoundary';
import { cn } from './cn';

type Route = 'config' | 'strategy' | 'simulation' | 'batch' | 'compare' | 'perf';

function parseHash(): Route {
  switch (window.location.hash) {
    case '#/strategy': return 'strategy';
    case '#/simulation': return 'simulation';
    case '#/batch': return 'batch';
    case '#/compare': return 'compare';
    case '#/perf': return 'perf';
    default: return 'config';
  }
}

const NAV_ITEMS: { route: Route; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { route: 'config', label: '场景配置', icon: Settings2 },
  { route: 'strategy', label: '调度策略', icon: Zap },
  { route: 'simulation', label: '仿真运行', icon: Play },
  { route: 'batch', label: '批量实验', icon: FlaskConical },
  { route: 'compare', label: '结果对比', icon: BarChart3 },
];

const PAGE_STAGE: Partial<Record<Route, string>> = {
  config: 'STEP 1 / 5',
  strategy: 'STEP 2 / 5',
  simulation: 'STEP 3 / 5',
  batch: 'STEP 4 / 5',
  compare: 'STEP 5 / 5',
};

function TopNav({ route }: { route: Route }) {
  const go = (r: Route) => { window.location.hash = `#/${r}`; };
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-4 gap-0 border-b border-border
        bg-[oklch(0.09_0.008_240/95%)] backdrop-blur-md"
    >
      <button onClick={() => go('config')} className="flex items-center gap-2 min-w-[180px]">
        <div className="flex items-center justify-center w-7 h-7 rounded bg-primary/15 border border-primary/30">
          <Activity className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="text-left">
          <span className="text-sm font-semibold tracking-tight text-foreground">电梯调度模拟</span>
          <span className="ml-2 text-[10px] font-mono text-primary/70 tracking-widest uppercase">
            {PAGE_STAGE[route] ?? 'M0 PERF'}
          </span>
        </div>
      </button>

      <nav className="flex items-stretch flex-1 h-full" role="navigation" aria-label="主导航">
        {NAV_ITEMS.map(({ route: r, label, icon: Icon }) => {
          const active = route === r;
          return (
            <button
              key={r}
              onClick={() => go(r)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex items-center gap-1.5 px-4 h-full text-sm transition-colors duration-150',
                active ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:block">{label}</span>
              {active && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-t" />}
            </button>
          );
        })}
      </nav>

      <button
        onClick={() => go('perf')}
        className={cn(
          'flex items-center gap-1.5 px-3 h-full text-xs text-muted-foreground hover:text-foreground transition-colors',
          route === 'perf' && 'text-primary',
        )}
        title="性能原型 (M0)"
      >
        <Gauge className="w-3.5 h-3.5" />
        <span className="hidden md:block">性能</span>
      </button>

      <div className="flex items-center gap-1.5 ml-2 text-[11px] font-mono text-muted-foreground min-w-[120px] justify-end">
        <span className="w-1.5 h-1.5 rounded-full bg-chart-3 animate-pulse" />
        <span>SIM READY</span>
      </div>
    </header>
  );
}

export function App() {
  const [route, setRoute] = useState<Route>(parseHash());

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      <TopNav route={route} />
      <main className="pt-14" role="main">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
          <ErrorBoundary>
            {route === 'config' && <ScenarioConfigPage />}
            {route === 'strategy' && <StrategyPage />}
            {route === 'simulation' && <SimulationPage />}
            {route === 'batch' && <BatchPage />}
            {route === 'compare' && <ComparePage />}
            {route === 'perf' && <PerfBenchmark />}
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
